import { describeVariantLines } from '@sitzmark/core';
import type { LabelItem } from '../purchasing/labels.js';

/**
 * Deciding what to print when labels are run from a style rather than from a
 * delivery (the "print labels from Stock" item, found 2026-08-28).
 *
 * The receiving bench knows the answer already — one label per unit that just
 * arrived. Nothing else did, because `useLabelPrinter` was reachable from that
 * one screen, so a style the shop never received through us had **no route to a
 * label at all**. After the inventory import that is most of the catalog: 90.4%
 * of their rows carry no UPC, so for nine items in ten the label we print is the
 * only barcode the item will ever have.
 *
 * Pure, and tested here rather than through the dialog: what is interesting is
 * which variants get how many labels, and that is arithmetic (§2 of the
 * verification standard).
 */

export interface LabelableVariant {
  readonly id: string;
  readonly sku: string;
  readonly barcode: string | null;
  readonly colorName: string | null;
  readonly sizeLabel: string | null;
  readonly altSize: string | null;
  readonly retailCents: number;
  readonly cardPriceCents: number;
  readonly active: boolean;
  readonly stock: { readonly onHand: number };
}

/**
 * The variants worth offering.
 *
 * A deactivated size is off the register and out of search, so a label for it
 * would be a barcode that scans to nothing sellable — which is worse than no
 * label, because it looks like the item is simply broken. They are left out and
 * the dialog says how many, rather than quietly shortening the list (§2.2-10).
 */
export function labelableVariants(
  variants: readonly LabelableVariant[],
): readonly LabelableVariant[] {
  return variants.filter((variant) => variant.active);
}

/**
 * One label per unit on the shelf, which is the answer nine times in ten.
 *
 * The alternative default — one per size — is what you want for a rail tag, and
 * it is one press away. Defaulting to stock means the common case is
 * confirmation rather than data entry (§2.2-5), and it is self-limiting: a style
 * with nothing on hand proposes nothing, which is right, because labels for
 * goods that have not arrived get lost before the goods do.
 */
export function quantitiesFromStock(variants: readonly LabelableVariant[]): Record<string, number> {
  return Object.fromEntries(
    labelableVariants(variants).map((variant) => [variant.id, Math.max(variant.stock.onHand, 0)]),
  );
}

/** One per size — the rail tag or the bin. */
export function quantitiesOneEach(variants: readonly LabelableVariant[]): Record<string, number> {
  return Object.fromEntries(labelableVariants(variants).map((variant) => [variant.id, 1]));
}

export function quantitiesNone(variants: readonly LabelableVariant[]): Record<string, number> {
  return Object.fromEntries(labelableVariants(variants).map((variant) => [variant.id, 0]));
}

export function totalLabels(quantities: Record<string, number>): number {
  return Object.values(quantities).reduce((sum, quantity) => sum + Math.max(quantity, 0), 0);
}

/**
 * The run itself.
 *
 * Zero-quantity variants drop out rather than being printed once "just in case":
 * a run of 300 labels with 40 nobody asked for is 40 labels stuck on the wrong
 * things, and the operator asked for a number.
 */
export function buildLabelItems(
  productName: string,
  variants: readonly LabelableVariant[],
  quantities: Record<string, number>,
): LabelItem[] {
  return labelableVariants(variants).flatMap((variant) => {
    const quantity = Math.trunc(quantities[variant.id] ?? 0);
    if (quantity <= 0) return [];

    return [
      {
        sku: variant.sku,
        barcode: variant.barcode,
        ...describeVariantLines({
          productName,
          colorName: variant.colorName,
          sizeLabel: variant.sizeLabel,
          altSize: variant.altSize,
        }),
        quantity,
        retailCents: variant.retailCents,
        cardPriceCents: variant.cardPriceCents,
      },
    ];
  });
}
